<template>
  <div>
    <slot name="header" />

    <div v-if="roadmapPerformance">
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 gap-2 mb-4" :class="kpiGridClass">
        <KpiCard v-if="singleStore" :value="yesterdayPerformance?.salesCurrent ?? 0" :label="t('sales_yesterday')" />
        <KpiCard :value="todaySales?.salesCurrent ?? 0" :label="t('sales_today')" />
        <KpiCard
          :value="monthlyPerformance?.salesCurrent ?? 0"
          :label="t('sales_this_month')"
          :variation="monthlyPerformance?.variation"
        />
        <KpiCard v-if="!singleStore" :label="t('active_stores')">
          <template #value>
            <span class="text-red-600">{{ activeStores?.active ?? 0 }}</span>
            <span class="text-sm font-bold">/{{ activeStores?.total ?? 0 }}</span>
          </template>
        </KpiCard>
        <KpiCard
          v-if="isOrganizationOrStoreAdmin"
          :value="formattedRemuneration"
          :label="t('remuneration')"
          :variation="remuneration?.variation"
        />
      </div>

      <!-- Objective -->
      <ObjectiveBlock :title="t('objective_title')" :monthlyPerformance="monthlyPerformance" />

      <!-- Rankings -->
      <div class="space-y-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopStoresBlock v-if="storeRanking" :storeRanking="storeRanking || []" />
          <TopSellersBlock
            v-if="sellerRanking"
            :sellerRanking="sellerRanking || []"
            viewMoreLink="/admin/retributions"
          />
        </div>

        <TrainingBlock />

        <ComplianceBlock
          v-if="hasSeatsOrFurniture && compliance && compliance.regionRate != null"
          :compliance="compliance"
          :multiStore="!singleStore"
        />

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <PendingClaimsBlock claimsRoute="/admin/claims?tab=action-required" :storeChannels="[]" />
          <SupportCallout />
        </div>
      </div>
    </div>
    <Loading v-else />
  </div>
</template>

<script>
import KpiCard from "@/components/roadmap/KpiCard.vue";
import ObjectiveBlock from "@/components/roadmap/ObjectiveBlock.vue";
import TopStoresBlock from "@/components/roadmap/TopStoresBlock.vue";
import TopSellersBlock from "@/components/roadmap/TopSellersBlock.vue";
import ComplianceBlock from "@/components/roadmap/ComplianceBlock.vue";
import TrainingBlock from "@/components/roadmap/TrainingBlock.vue";
import PendingClaimsBlock from "@/components/home/PendingClaimsBlock.vue";
import SupportCallout from "@/components/home/SupportCallout.vue";
import AnalyticsService from "@/api/services/analytics";
import { Loading, toCamelCase, formatPrice } from "@estaly/ui";
import { mapGetters } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: {
    KpiCard,
    ObjectiveBlock,
    TopStoresBlock,
    TopSellersBlock,
    ComplianceBlock,
    TrainingBlock,
    PendingClaimsBlock,
    SupportCallout,
    Loading,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data: () => ({
    roadmapPerformance: null,
    yesterdayPerformance: null,
    monthlyPerformance: null,
    sellerRanking: null,
    storeRanking: null,
    activeStores: null,
    todaySales: null,
    remuneration: null,
    compliance: {},
    singleStore: false,
  }),
  computed: {
    ...mapGetters("auth", ["isOrganizationOrStoreAdmin"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),
    dataSource() {
      return this.hasSeatsOrFurniture ? "gross_sales" : "plan_sales";
    },
    formattedRemuneration() {
      return formatPrice(this.remuneration?.current ?? 0);
    },
    kpiCount() {
      let count = 2; // today + this month (always visible)
      if (this.singleStore) count++; // yesterday
      if (!this.singleStore) count++; // active stores
      if (this.isOrganizationOrStoreAdmin) count++; // remuneration
      return count;
    },
    kpiGridClass() {
      return this.kpiCount >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const response = await AnalyticsService.getAdminRoadmapPerformance(this.dataSource);
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.roadmapPerformance = data;
      this.yesterdayPerformance = data.yesterdayPerformance;
      this.monthlyPerformance = data.monthlyPerformance;
      this.sellerRanking = data.sellerRanking;
      this.storeRanking = data.storeRanking;
      this.activeStores = data.activeStores || null;
      this.todaySales = data.todaySales || null;
      this.remuneration = data.remuneration || null;
      this.compliance = data.compliance || {};
      this.singleStore = data.singleStore || false;
    },
  },
};
</script>

<i18n src="./AdminBriefOverview.i18n.json"></i18n>
